import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { resolvePatientByAthenaId } from 'services/athena-embed';
import {
  ATHENA_SIDECAR_PATIENT_NOT_FOUND_ROUTE,
  athenaPatientRoute,
  athenaSidecarCarePlanRoute,
  isAllowedAthenaOrigin,
  isAthenaSidecarLaunch,
  parsePatientContextEvent,
} from 'utils/athena-embed';
import { useLoggedInUserType } from './userHooks';

type RouterHistory = ReturnType<typeof useHistory>;

// Where SSO sends a launch whose athena patient has no local match.
const ATHENA_PATIENT_ERROR_ROUTE = '/athena-error/patient';

const navigateToAthenaPatient = async (history: RouterHistory, athenaId: string, userType?: string): Promise<void> => {
  // A sidecar launch keeps landing on care plans as athena switches patients.
  const isSidecar = isAthenaSidecarLaunch(history.location.search);
  const patientRoute = isSidecar ? athenaSidecarCarePlanRoute : athenaPatientRoute;
  const errorRoute = isSidecar ? ATHENA_SIDECAR_PATIENT_NOT_FOUND_ROUTE : ATHENA_PATIENT_ERROR_ROUTE;
  try {
    const { data } = await resolvePatientByAthenaId(athenaId);
    history.push(data?.id ? patientRoute(data.id, userType) : errorRoute);
  } catch {
    history.push(errorRoute);
  }
};

// Handles a single postMessage: navigates to the selected patient only for
// trusted athena origins carrying a `patientContextChanged` payload.
const handlePatientContextMessage = (history: RouterHistory, event: MessageEvent, userType?: string): void => {
  if (!isAllowedAthenaOrigin(event.origin)) {
    return;
  }
  const updatedPatient = parsePatientContextEvent(event.data)?.updatedPatient;
  if (updatedPatient) {
    navigateToAthenaPatient(history, updatedPatient, userType);
  }
};

// Listens for athenaOne's `patientContextChanged` embedded-app message and
// navigates the SPA to the newly selected patient. No-op outside the athenaOne
// iframe, since only trusted athena origins pass the origin check.
export const useAthenaPatientContext = (): void => {
  const history = useHistory();
  const userType = useLoggedInUserType();

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => handlePatientContextMessage(history, event, userType);
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [history, userType]);
};
